import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listDeviceConditionTemplates,
  updateDeviceConditionTemplate,
  type DeviceConditionTemplate,
  type TemplateItem,
} from '@/lib/api/device-condition-templates.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

const DEVICE_CATEGORIES = [
  { id: 'phone', label: '📱 Phone' },
  { id: 'laptop', label: '💻 Laptop' },
  { id: 'desktop', label: '🖥 Desktop' },
  { id: 'gaming_console', label: '🎮 Gaming Console' },
  { id: 'tablet', label: '📱 Tablet' },
  { id: 'other', label: '📦 Other' },
] as const;

function TemplateEditor({
  template,
  title,
}: {
  template: DeviceConditionTemplate | undefined;
  title: string;
}) {
  const queryClient = useQueryClient();
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [newItemName, setNewItemName] = useState('');

  useEffect(() => {
    if (template) {
      setItems([...template.items].sort((a, b) => a.order - b.order));
    } else {
      setItems([]);
    }
  }, [template?.id, template?.items]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!template) throw new Error('Template not found');
      const normalized = items.map((item, index) => ({ name: item.name, order: index + 1 }));
      return updateDeviceConditionTemplate(template.id, normalized);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-condition-templates'] });
      toastSuccess(`${title} saved`);
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function moveItem(index: number, direction: -1 | 1) {
    const next = index + direction;
    if (next < 0 || next >= items.length) return;
    setItems((current) => {
      const copy = [...current];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  }

  function removeItem(index: number) {
    setItems((current) => current.filter((_, i) => i !== index));
  }

  function addItem() {
    const name = newItemName.trim();
    if (!name) return;
    if (items.some((item) => item.name.toLowerCase() === name.toLowerCase())) {
      toastError('Item already exists');
      return;
    }
    setItems((current) => [...current, { name, order: current.length + 1 }]);
    setNewItemName('');
  }

  if (!template) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
        No template configured for this category.
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li
            key={`${item.name}-${index}`}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <span className="min-w-0 flex-1 font-medium text-slate-800">{item.name}</span>
            <button
              type="button"
              className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"
              onClick={() => moveItem(index, -1)}
              disabled={index === 0}
            >
              ↑
            </button>
            <button
              type="button"
              className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"
              onClick={() => moveItem(index, 1)}
              disabled={index === items.length - 1}
            >
              ↓
            </button>
            <button
              type="button"
              className="rounded-lg px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
              onClick={() => removeItem(index)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <input
          className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm"
          placeholder="Add item..."
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addItem();
            }
          }}
        />
        <button
          type="button"
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
          onClick={addItem}
        >
          + Add Item
        </button>
      </div>
      <button
        type="button"
        className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        Save Changes
      </button>
    </div>
  );
}

export function DeviceConditionTemplatesSettings() {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const templatesQuery = useQuery({
    queryKey: ['device-condition-templates'],
    queryFn: () => listDeviceConditionTemplates(),
  });

  const byCategory = useMemo(() => {
    const map = new Map<string, { conditions?: DeviceConditionTemplate; supplied?: DeviceConditionTemplate }>();
    for (const template of templatesQuery.data ?? []) {
      const entry = map.get(template.deviceCategory) ?? {};
      if (template.templateType === 'conditions') entry.conditions = template;
      if (template.templateType === 'supplied_items') entry.supplied = template;
      map.set(template.deviceCategory, entry);
    }
    return map;
  }, [templatesQuery.data]);

  return (
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div>
        <h3 className="text-lg font-semibold text-slate-950">Device Condition Templates</h3>
        <p className="mt-1 text-sm text-slate-500">
          Configure pre/post repair checklists and supplied items per device category.
        </p>
      </div>
      {templatesQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading templates...</p>
      ) : (
        <div className="space-y-2">
          {DEVICE_CATEGORIES.map((category) => {
            const expanded = expandedCategory === category.id;
            const templates = byCategory.get(category.id);
            return (
              <div key={category.id} className="rounded-xl border border-slate-200">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-slate-900"
                  onClick={() => setExpandedCategory(expanded ? null : category.id)}
                >
                  <span>{category.label}</span>
                  <span className="text-slate-400">{expanded ? '▼' : '▶'}</span>
                </button>
                {expanded ? (
                  <div className="space-y-4 border-t border-slate-200 p-4">
                    <TemplateEditor
                      template={templates?.conditions}
                      title="Conditions checklist items"
                    />
                    <TemplateEditor
                      template={templates?.supplied}
                      title="Supplied items checklist"
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
